import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import {
  AccessTime,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { getShifts, putShift } from '../../../lib/connector/shiftQueries';
import moment from 'moment';
import { useToast } from '../ToastProvider/ToastProvider';

interface AddShiftModalProps {
  open: boolean;
  onClose: () => void;
  selectedSchedule: any;
  addShiftFormData: any;
  setAddShiftFormData: (data: any) => void;
  shifts: any[];
  setShifts: (shifts: any[]) => void;
  positions: any[];
  setPositions: (positions: any[]) => void;
}

export default function AddShiftModal({ open, onClose, selectedSchedule, addShiftFormData, setAddShiftFormData, shifts, setShifts, positions, setPositions }: AddShiftModalProps) {
  const toast = useToast();
  const [formData, setFormData] = [addShiftFormData, setAddShiftFormData];

  const daysOfWeek = [
    { value: 'monday', label: 'Mon', fullLabel: 'Monday' },
    { value: 'tuesday', label: 'Tue', fullLabel: 'Tuesday' },
    { value: 'wednesday', label: 'Wed', fullLabel: 'Wednesday' },
    { value: 'thursday', label: 'Thu', fullLabel: 'Thursday' },
    { value: 'friday', label: 'Fri', fullLabel: 'Friday' },
    { value: 'saturday', label: 'Sat', fullLabel: 'Saturday' },
    { value: 'sunday', label: 'Sun', fullLabel: 'Sunday' }
  ];

  const handleChange = (e: { target: { name: any; value: any; }; }) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Add a specific handler for checkboxes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formData.daysOfWeek = []; // Clear daysOfWeek when toggling isRecurring
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked
    });
  };

  const handleDaysChange = (event: any, newDays: any) => {
    setFormData({
      ...formData,
      daysOfWeek: newDays
    });
  };

  const handleSubmit = () => {
    putShift(selectedSchedule.scheduleId, uuidv4(), formData.shiftName, formData.daysOfWeek, formData.startTime, formData.endTime,
      formData.position, formData.staffNeeded.toString(), [], formData.isRecurring, formData.shiftDate, formData.notes).then(() => {
        getShifts(selectedSchedule.scheduleId).then((shifts) => {
          setShifts(shifts);
        });
        toast.showSuccess("Shift created successfully.");
      });
    onClose();
    // Reset form
    setFormData({
      shiftName: '',
      daysOfWeek: [],
      startTime: '',
      endTime: '',
      position: '',
      staffNeeded: 1,
      isRecurring: true,
      shiftDate: moment().format('YYYY-MM-DD'),
      notes: ''
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <AccessTime />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Create New Shift
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add a new shift to the schedule
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>

          {/* Shift Name */}
          <TextField
            fullWidth
            label="Shift Name"
            name="shiftName"
            value={formData.shiftName}
            onChange={handleChange}
            placeholder="e.g., Morning Shift, Evening Shift"
            required
          />

          {/* Days of Week Selection */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select Days of Week *
            </Typography>
            <ToggleButtonGroup
              value={formData.daysOfWeek}
              onChange={handleDaysChange}
              disabled={formData.isRecurring === false}
              fullWidth
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 1,
                '& .MuiToggleButton-root': {
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }
                }
              }}
            >
              {daysOfWeek.map((day) => (
                <ToggleButton
                  key={day.value}
                  value={day.value}
                  aria-label={day.fullLabel}
                >
                  {day.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {formData.daysOfWeek.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.daysOfWeek.map((day: string) => {
                  const dayInfo = daysOfWeek.find(d => d.value === day);
                  return (
                    <Chip
                      key={dayInfo ? dayInfo.value : String(day)}
                      label={dayInfo ? dayInfo.fullLabel : String(day)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Start and End Time */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Time"
                name="startTime"
                type="time"
                value={formData.startTime}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Time"
                name="endTime"
                type="time"
                value={formData.endTime}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
          </Grid>

          {/* Position/Role */}
          <FormControl fullWidth>
            <InputLabel>Position/Role</InputLabel>
            <Select
              name="position"
              value={formData.position}
              onChange={handleChange}
              label="Position/Role"
              required
            >
              {positions.map((position) => (
                <MenuItem key={position.position} value={position.position}>
                  {position.positionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Number of People Needed */}
          <TextField
            fullWidth
            label="Number of People Needed"
            name="staffNeeded"
            type="number"
            value={formData.staffNeeded}
            onChange={handleChange}
            InputProps={{
              inputProps: { min: 1, max: 50 }
            }}
            helperText="How many employees do you need for this shift?"
            required
            color="primary"
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {/* Recurring Shift Checkbox */}
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isRecurring"
                      checked={Boolean(formData.isRecurring)}
                      onChange={handleCheckboxChange}  // Use the checkbox-specific handler
                      color="primary"
                    />
                  }
                  label="Recurring Shift"
                />
                <FormHelperText>Check if this shift repeats weekly</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Shift Date"
                name="shiftDate"
                type="date"
                disabled={formData.isRecurring}
                value={formData.shiftDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Select date for one-time shift"
                required={!formData.isRecurring}
              />
            </Grid>
          </Grid>



          {/* Shift Duration Display */}
          {formData.startTime && formData.endTime && (
            <Box
              sx={{
                bgcolor: 'primary.50',
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: 'primary.100'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ClockIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight={600} color="primary">
                  Shift Duration
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const start = new Date(`2000-01-01T${formData.startTime}`);
                  const end = new Date(`2000-01-01T${formData.endTime}`);
                  let diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                  if (diff < 0) diff += 24;
                  return `${diff.toFixed(1)} hours`;
                })()}
              </Typography>
            </Box>
          )}

          {/* Notes (Optional) */}
          <TextField
            fullWidth
            label="Notes (Optional)"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={3}
            placeholder="Any additional information about this shift..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !formData.shiftName ||
            (formData.daysOfWeek.length === 0 && formData.isRecurring) ||
            !formData.startTime ||
            !formData.endTime ||
            !formData.position
          }
        >
          Create Shift
        </Button>
      </DialogActions>
    </Dialog>
  );
}
